import React, { useState } from 'react';
import { Check, Minus } from 'lucide-react';
import styles from './Pricing.module.css';
import { STRIPE_LINKS } from '../config/stripe';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

const PLANS = [
  {
    name: 'Starter',
    desc: 'Designed for new businesses establishing their initial presence on Reddit. This plan focuses on light engagement activity and foundational visibility.',
    monthly: 49,
    annual: 39,
    popular: false,
    features: [
      { text: '300 post upvotes per month', included: true },
      { text: '100 comment upvotes per month', included: true },
      { text: '5 custom written comments per month', included: true },
      { text: 'Targeting of 1 subreddit', included: true },
      { text: 'Scheduled drip delivery', included: true },
      { text: 'Monthly performance summary', included: true },
      { text: 'Standard support', included: true },
      { text: 'Single subreddit focus only', included: true },
    ],
  },
  {
    name: 'Growth',
    desc: 'Designed for growing businesses that require consistent Reddit engagement and improved visibility across targeted communities.',
    monthly: 99,
    annual: 79,
    popular: true,
    features: [
      { text: '600 post upvotes per month', included: true },
      { text: '250 comment upvotes per month', included: true },
      { text: '12 custom written comments per month', included: true },
      { text: 'Targeting of 2 subreddits', included: true },
      { text: 'Scheduled drip delivery', included: true },
      { text: 'Bi weekly performance reporting', included: true },
      { text: 'Priority support', included: true },
      { text: 'No white label reporting', included: false },
    ],
  },
  {
    name: 'Professional',
    desc: 'Designed for established brands aiming to build authority and dominate niche relevant Reddit communities through consistent engagement.',
    monthly: 199,
    annual: 159,
    popular: false,
    features: [
      { text: '2000 post upvotes per month', included: true },
      { text: '700 comment upvotes per month', included: true },
      { text: '35 custom written comments per month', included: true },
      { text: 'Targeting of 5 subreddits', included: true },
      { text: 'Faster delivery scheduling', included: true },
      { text: 'Weekly performance reporting', included: true },
      { text: 'Priority support', included: true },
      { text: 'White label PDF reporting included', included: true },
    ],
  },
  {
    name: 'Agency',
    desc: 'Designed for agencies and teams managing multiple clients who need scalable Reddit engagement infrastructure.',
    monthly: 399,
    annual: 319,
    popular: false,
    features: [
      { text: '6000 post upvotes per month', included: true },
      { text: '2000 comment upvotes per month', included: true },
      { text: '100 custom written comments per month', included: true },
      { text: 'Unlimited subreddit targeting', included: true },
      { text: 'Multi client support structure', included: true },
      { text: 'White label reporting system', included: true },
      { text: 'API access', included: true },
      { text: 'Dedicated account support', included: true },
    ],
  },
];

const KARMA_PLANS = [
  {
    name: 'Starter Package',
    karma: '200 to 500 karma',
    price: 29,
    best: false,
    detail: 'Designed for basic profile strengthening and initial credibility building. Delivery within 7 to 10 days. Mixed post and comment activity. Single account setup.',
  },
  {
    name: 'Trust Package',
    karma: '1000 to 2000 karma',
    price: 59,
    best: true,
    detail: 'Designed for building credible and natural looking Reddit activity history. Delivery within 14 to 21 days. Comment focused engagement. Subreddit tone matching behavior. Single account setup.',
  },
  {
    name: 'Power Package',
    karma: '3000 to 5000 karma',
    price: 129,
    best: false,
    detail: 'Designed for stronger presence building across multiple communities. Delivery within 30 to 45 days. Multi subreddit engagement pattern. Single account setup.',
  },
  {
    name: 'Authority Package',
    karma: '8000 to 12000 karma',
    price: 249,
    best: false,
    detail: 'Designed for advanced credibility building with long term organic style activity patterns. Delivery within 60 to 90 days. Organic behavior simulation. Aged activity pattern structure. Single account setup.',
  },
];

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const headRef = useScrollAnimation();
  const plansRef = useStaggerAnimation();
  const karmaRef = useScrollAnimation();
  const karmaPlansRef = useStaggerAnimation();

  return (
    <section className={styles.section} id="pricing">
      <div className="container">
        {/* Head */}
        <div className={`${styles.head} fade-up`} ref={headRef}>
          <div className="section-label">Pricing</div>
          <h2 className="section-title">
            Transparent pricing.
            <br />
            No surprises.
          </h2>
          <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
            All plans include drip delivery, aged accounts, and dashboard access. Cancel anytime.
          </p>
        </div>

        {/* Billing toggle */}
        <div className={styles.toggle}>
          <span className={`${styles.toggleLabel} ${!isAnnual ? styles.active : ''}`}>
            Monthly
          </span>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={isAnnual}
              onChange={() => setIsAnnual((v) => !v)}
            />
            <div className={styles.track} />
            <div className={styles.thumb} />
          </label>
          <span className={`${styles.toggleLabel} ${isAnnual ? styles.active : ''}`}>
            Annual
          </span>
          <span className={styles.savePill}>Save 20%</span>
        </div>

        {/* Plans */}
        <div className="section-label" style={{ marginBottom: 16 }}>
          Reddit Engagement Monthly Subscription
        </div>
        <div className={styles.plansGrid} ref={plansRef}>
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`${styles.planCard} ${plan.popular ? styles.popular : ''} stagger-child`}
            >
              {plan.popular && (
                <div className={styles.popularBadge}>Most popular</div>
              )}
              <div className={styles.planName}>{plan.name}</div>
              <div className={styles.planDesc}>{plan.desc}</div>
              <div className={styles.planPrice}>
                <div>
                  <span className={styles.currency}>$</span>
                  <span className={styles.amount}>
                    {isAnnual ? plan.annual : plan.monthly}
                  </span>
                </div>
                <div className={styles.period}>
                  /month{' '}
                  {isAnnual && (
                    <span className={styles.was}>${plan.monthly}</span>
                  )}
                </div>
              </div>
              <ul className={styles.planFeatures}>
                {plan.features.map((f) => (
                  <li key={f.text} className={styles.planFeature}>
                    <span className={f.included ? styles.check : styles.dash}>
                      {f.included ? (
                        <Check size={14} strokeWidth={3} />
                      ) : (
                        <Minus size={14} strokeWidth={3} />
                      )}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => {
                  const key = `${plan.name.toLowerCase()}_${isAnnual ? 'annual' : 'monthly'}`;
                  const url = STRIPE_LINKS[key];
                  if (url) window.open(url, '_blank');
                }}
                className={`${styles.planBtn} ${plan.popular ? styles.planBtnPrimary : ''}`}
                style={{ width: '100%', border: 'none', cursor: 'pointer', outline: 'none' }}
              >
                Get {plan.name}
              </button>
            </div>
          ))}
        </div>

        {/* Karma section */}
        <div className={`${styles.karmaSection} fade-up`} ref={karmaRef}>
          <div className={styles.karmaHead}>
            <div>
              <div className={styles.karmaTag}>Standalone service</div>
              <h3 className={styles.karmaTitle}>Karma Building Package</h3>
              <p className={styles.karmaSub}>
                Your Reddit account needs karma to unlock restricted communities and gain trust.
                We naturally build this credibility for your accounts over time. You provide the profile URL, and we handle the growth process safely.
              </p>
            </div>
            <div className={styles.karmaNote}>
              <div>One time payment</div>
              <div>No recurring charges</div>
            </div>
          </div>

          <div className={styles.karmaPlans} ref={karmaPlansRef}>
            {KARMA_PLANS.map((k) => (
              <div
                key={k.name}
                className={`${styles.karmaPlan} ${k.best ? styles.karmaBest : ''} stagger-child`}
              >
                {k.best && <div className={styles.karmaBestBadge}>Best value</div>}
                <div className={styles.karmaPlanName}>{k.name}</div>
                <div className={styles.karmaAmount}>{k.karma}</div>
                <div className={styles.karmaPrice}>${k.price}</div>
                <div className={styles.karmaPriceSub}>One time fee</div>
                <div className={styles.karmaDetail}>{k.detail}</div>
                <button 
                  onClick={() => {
                    const key = `karma_${k.name.toLowerCase()}`;
                    const url = STRIPE_LINKS[key];
                    if (url) window.open(url, '_blank');
                  }}
                  className={styles.planBtn}
                  style={{ width: '100%', marginTop: '16px', border: 'none', cursor: 'pointer', outline: 'none' }}
                >
                  Get {k.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
